import * as React from 'react';
import styled from 'styled-components';
import { times } from 'lodash';
import shortid from 'shortid';
import posed, { PoseGroup } from 'react-pose';

import {
	TicketIcon,
	Title,
	AddIcon,
	Button,
	CheckIcon,
	ErrorIcon,
	Typography,
} from '../material-ui';
import styleObject from '../style-object';
import { WithData, client } from '../apollo-utils';
import { CategorySelection, QRCodeCard, SelectButton, Poster, Alert, Infobox } from '../components';
import { CurrentMoviesQuery, BookTicketBonuspointsMutation } from '../queries';

const categories = [
	...times(5, (i) => i + 1).map((i) => ({ label: `${i}`, value: i })),
	{ label: '6+', value: 6 },
];

const Wrapper = styled.div`
	flex: 1;
	padding: 3rem 2rem;
`;

const transition = { ease: 'easeInOut', duration: 300 };
const PoseWrapper = posed.div({
	enter: { opacity: 1, scale: 1, originX: 0, originY: 0, transition },
	exit: { opacity: 0, scale: 0.2, transition },
	flip: { transition },
});

const SectionSeparator = styled.div`
	margin: 2rem 0;
	border-bottom: 3px solid lightgray;
`;

const PosterWall = styled(PoseWrapper)`
	display: flex;
	flex-flow: row wrap;
	margin: 0 -0.5rem;
`;

const MovieSelectWrapper = styled.div`
	display: flex;
	margin: 1rem 0;
	height: 4rem;
	align-items: center;
`;

const BonusCard = styled(PoseWrapper)`
	margin: 1rem 0;
`;

const SubmitWrapper = styled.div`
	display: flex;
	flex-direction: row;
	margin: 2rem 0;
`;

const iconBulletStyle = styleObject`
	margin-right: 0.5rem;
	font-size: 1.2rem;
	vertical-align: text-top;
`;

const ErrorItem = styled.p`
	font-weight: bold;
`;

const iconStyle = styleObject`
	margin-right: 0.5rem;
`;

const labelStyle = styleObject`
	display: flex;
`;

const sendMutation = async (variables) => {
	try {
		const result = await client.mutate({
			mutation: BookTicketBonuspointsMutation,
			variables,
			errorPolicy: 'all',
		});
		return result;
	} catch (e) {
		if (e.networkError) {
			return { errors: [{ message: 'NETWORK_ERROR' }] };
		} else throw e;
	}
};

const getInitialState = () => ({
	alert: null,
	movieSelection: true,
	booking: {
		movie: null,
		groupSize: null,
		customers: [{ id: shortid.generate(), user: null, category: null }],
	},
});

export default class CollectScreen extends React.Component {
	static label = (
		<span style={labelStyle}>
			<TicketIcon style={iconStyle} />
			Sammeln
		</span>
	);

	state = getInitialState();
	qrRefs = {};

	setQrRefById = (id) => (node) => {
		this.qrRefs[id] = node;
	};

	handleToggleMovieSelection = () =>
		this.setState((state) => ({ movieSelection: !state.movieSelection }));

	selectFirstIncompleteCustomer = () => {
		const { customers } = this.state.booking;
		const customerId = customers.reduce((curr, { user, id }) => (!user && !curr ? id : curr), null);
		if (customerId) this.qrRefs[customerId].focus();
	};

	handlePosterSelection = (movie) =>
		this.setState(
			(state) => ({ booking: { ...state.booking, movie }, movieSelection: false }),
			() => {
				if (this.state.booking.groupSize) this.selectFirstIncompleteCustomer();
			}
		);

	handleGroupsizeSelection = (groupSize) =>
		this.setState(
			(state) => ({ booking: { ...state.booking, groupSize } }),
			() => {
				const { customers, movie } = this.state.booking;
				if (groupSize.value < customers.length) {
					times(customers.length - groupSize.value, (i) => i + 1).forEach((i) =>
						this.handleRemoveCustomer(customers[customers.length - i].id)
					);
				}
				if (movie) this.selectFirstIncompleteCustomer();
			}
		);

	handleCustomerValueSet = ({ id, key, value }) =>
		this.setState((state) => {
			const customers = [...state.booking.customers];
			customers[customers.findIndex((c) => c.id === id)][key] = value;
			return { booking: { ...state.booking, customers } };
		});

	handleAddCustomer = () => {
		const id = shortid.generate();
		this.setState(
			(state) => {
				const customers = [...state.booking.customers];
				const { category } = customers[customers.length - 1] || {};
				customers.push({ id, user: null, category });
				return {
					booking: { ...state.booking, customers },
				};
			},
			() => setTimeout(() => this.qrRefs[id].focus(), 50)
		);
	};

	handleRemoveCustomer = (id) =>
		this.setState((state) => {
			const customers = [...state.booking.customers];
			customers.splice(
				customers.findIndex((c) => c.id === id),
				1
			);
			return { booking: { ...state.booking, customers } };
		});

	handleReset = () => this.setState(getInitialState());

	handleSubmit = () => {
		const {
			booking: { movie, groupSize, customers },
		} = this.state;
		const payloads = customers.map(({ user, category }) => ({
			amount: category.bonusPoints,
			movieId: movie.id,
			groupSize: groupSize.value,
			userQrCode: user.bonusProgramMembership.qrCode,
		}));
		this.setState({
			alert: {
				title: 'Punkte Buchen?',
				description: [
					`Film: ${movie.title}`,
					`Gruppengröße: ${groupSize.value}`,
					...customers.map(
						({ user, category }) =>
							`
							Nutzer: ${user.name}
							Punkte: ${category.bonusPoints} (${category.name})`
					),
				].join('\n'),
				onConfirm: async () => {
					this.setState({ alert: { loading: true, onAbort: null } });
					const results = await Promise.all(payloads.map(sendMutation));
					if (
						results.every(({ data }) => data && data.result && data.result.status === 'SUCCESS')
					) {
						this.setState({
							alert: {
								title: 'Punkte gebucht',
								description: 'Die Bonuspunkte wurden erfolgreich gebucht.',
								onConfirm: this.handleReset,
								onAbort: null,
							},
						});
					} else {
						this.setState({
							alert: {
								title: 'Nicht alle gebucht',
								description: results
									.map(({ data, errors }, i) => {
										const userName = `Nutzer: ${customers[i].user.name}  —  `;
										if (data && data.result && data.result.status) {
											switch (data.result.status) {
												case 'SUCCESS':
													return userName + 'Erfolgreich gebucht';
												case 'DUPLICATE_BOOKING':
													return userName + 'Doppelbuchung unterbunden';
												default:
													return (
														userName +
														`Unerwarteter Fehler: status-${data.result.status} ` +
														JSON.stringify(errors)
													);
											}
										} else return userName + 'Unerwarteter Fehler: ' + JSON.stringify(errors);
									})
									.join('\n'),
								onConfirm: this.handleCloseAlert,
								onAbort: null,
							},
						});
					}
				},
			},
		});
	};

	handleCloseAlert = () => this.setState({ alert: null });

	render = () => {
		const { cinemaId, cameraScannerEnabled } = this.props;
		const { alert, movieSelection, booking } = this.state;
		const { movie, groupSize, customers } = booking;
		const lastCustomer = customers[customers.length - 1];
		const completed =
			movie &&
			groupSize &&
			customers.length > 0 &&
			customers.every(({ user, category }) => user && category);
		return (
			<WithData
				query={CurrentMoviesQuery}
				// eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
				variables={{ cinemaId }}
				pollInterval={300000}
			>
				{({ cinema: { movies } }) => (
					<Wrapper>
						<Alert {...{ onAbort: this.handleCloseAlert, ...alert }} />
						<PoseGroup animateOnMount>
							<PoseWrapper key={1}>
								<Title>Film:</Title>
								<MovieSelectWrapper>
									{booking.movie ? (
										<SelectButton
											image={booking.movie.poster}
											title={booking.movie.title}
											onClick={this.handleToggleMovieSelection}
										/>
									) : (
										<Typography variant="caption">FILM AUSWÄHLEN</Typography>
									)}
								</MovieSelectWrapper>
							</PoseWrapper>
							{movieSelection && (
								<PosterWall key={'12'} movies={movies}>
									{movies.map(({ id, title, poster }) => (
										<Poster
											key={id}
											id={id}
											title={title}
											poster={poster}
											onClick={this.handlePosterSelection}
										/>
									))}
								</PosterWall>
							)}
							<PoseWrapper key={2}>
								<SectionSeparator />
								<Title>Gruppengröße:</Title>
								<CategorySelection
									categories={categories}
									value={booking.groupSize && booking.groupSize.value}
									onCategorySelected={this.handleGroupsizeSelection}
								/>
								<SectionSeparator />
								<Title>Bonuskarten:</Title>
							</PoseWrapper>
							{customers.map((customer) => (
								<BonusCard key={customer.id}>
									<QRCodeCard
										{...customer}
										categorySelection
										cameraScannerEnabled={cameraScannerEnabled}
										onDelete={this.handleRemoveCustomer}
										onValueSet={this.handleCustomerValueSet}
										cinemaId={cinemaId}
										ref={this.setQrRefById(customer.id)}
									/>
								</BonusCard>
							))}
							{(!groupSize || groupSize.label === '6+' || customers.length < groupSize.value) && (
								<PoseWrapper key={'add-button'}>
									<Button
										variant="contained"
										onClick={this.handleAddCustomer}
										disabled={lastCustomer && !(lastCustomer.user && lastCustomer.category)}
									>
										<AddIcon />
										Weitere Hinzufügen
									</Button>
								</PoseWrapper>
							)}
							<PoseWrapper key={3}>
								<SectionSeparator />
								<SubmitWrapper>
									<Button style={iconStyle} onClick={this.handleReset}>
										Zurücksetzten
									</Button>
									<Infobox
										content={
											!completed &&
											[
												movie ? null : 'Kein Film ausgewählt',
												groupSize ? null : 'Angabe zur Gruppengröße fehlt',
												customers.length > 0 ? null : 'Keine Bonuskarten gescannt',
												customers.every(({ user, category }) => user && category)
													? null
													: 'Unvollständige Angabe zu Bonuskarte',
											].map(
												(text) =>
													text && (
														<ErrorItem key={text}>
															<ErrorIcon style={iconBulletStyle} />
															{text}
														</ErrorItem>
													)
											)
										}
									>
										<Button
											variant="contained"
											color="primary"
											disabled={!completed}
											onClick={this.handleSubmit}
										>
											{completed ? (
												<CheckIcon style={iconStyle} />
											) : (
												<ErrorIcon style={iconStyle} />
											)}
											Punkte Buchen
										</Button>
									</Infobox>
								</SubmitWrapper>
							</PoseWrapper>
						</PoseGroup>
					</Wrapper>
				)}
			</WithData>
		);
	};
}



// WEBPACK FOOTER //
// ./src/screens/Collect.js